import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'

import { useProtocolsList, Protocol } from '@services/protocols/protocols.hook'
import { PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import { protocolsDate } from '@services/date/date.models'
import { useAtom } from 'jotai'
import 'dayjs/locale/pl'
import { useState } from 'react'

function SelectProtocolByMonth({ onDateClick }: { onDateClick: (date: string) => void }) {
  const [date] = useAtom(protocolsDate)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredProtcols, setFilteredProtocols] = useState<any[]>([])

  const daysInMonth = new Array(dayjs(date).daysInMonth()).fill(null).map((_, index) =>
    dayjs(date)
      .set('date', index + 1)
      .toISOString()
  )

  const { protocolsList } = useProtocolsList()

  const protocols = protocolsList
    .filter(({ leaseStartDate, effectiveLeaseEnd, protocolType }) => {
      if (protocolType === PROTOCOL_TYPE.acceptance) {
        return !!leaseStartDate
      }

      return !!effectiveLeaseEnd
    })
    .filter(({ leaseStartDate, effectiveLeaseEnd }) => {
      const day = dayjs(date)

      if (leaseStartDate) {
        return day.isSame(leaseStartDate, 'month') && day.isSame(leaseStartDate, 'year')
      }

      return day.isSame(effectiveLeaseEnd, 'month') && day.isSame(effectiveLeaseEnd, 'year')
    })
    .reduce(
      (acc, curr) => {
        if (curr.protocolType === PROTOCOL_TYPE.acceptance) {
          return {
            acceptance: [...acc.acceptance, curr],
            delivery: acc.delivery
          }
        }

        return {
          delivery: [...acc.delivery, curr],
          acceptance: acc.acceptance
        }
      },
      {
        acceptance: [] as Protocol[],
        delivery: [] as Protocol[]
      }
    )

  const protocolsInMonth = daysInMonth.map((day) => {
    return {
      date: day,
      acceptance: protocols.acceptance.filter(
        ({ leaseStartDate, effectiveLeaseEnd }) =>
          dayjs(day).isSame(leaseStartDate, 'day') || dayjs(day).isSame(effectiveLeaseEnd, 'day')
      ),
      delivery: protocols.delivery.filter(
        ({ effectiveLeaseEnd, leaseStartDate }) =>
          dayjs(day).isSame(effectiveLeaseEnd, 'day') || dayjs(day).isSame(leaseStartDate, 'day')
      )
    }
  })

  const handleSearch = (event: any) => {
    const value = event.target.value
    setSearchTerm(value)

    const searchResults = protocolsInMonth.filter((item: any) => {
      const foundInAcceptance = item.acceptance.some((acceptanceItem: any) => {
        return acceptanceItem.flat.includes(value)
      })

      const foundInDelivery = item.delivery.some((deliveryItem: any) => {
        return deliveryItem.flat.includes(value)
      })

      return foundInAcceptance || foundInDelivery
    })

    setFilteredProtocols(searchResults)
  }

  return (
    <Paper sx={{ flex: 1, width: '100%', typography: 'body1' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography p={2}>{dayjs(date).locale('pl').format('MMMM YYYY')}</Typography>
        <TextField
          label='Szukaj lokalu'
          variant='outlined'
          margin='normal'
          value={searchTerm}
          onChange={handleSearch}
          sx={{ paddingRight: '20px' }}
        />
      </Box>
      <Box>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell align='center'>Wprowadzki (Niezrobione)</TableCell>
                <TableCell align='center'>Wyprowadzki (Niezrobione)</TableCell>
              </TableRow>
            </TableHead>
            {searchTerm.length > 0 ? (
              <TableBody>
                {filteredProtcols.map((row: any) => (
                  <TableRow
                    key={row.date}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => onDateClick(row.date)}
                  >
                    <TableCell component='th' scope='row'>
                      {dayjs(row.date).format('DD.MM.YYYY')}
                    </TableCell>
                    <TableCell align='center'>
                      {row.acceptance.length} ({row.acceptance.length})
                    </TableCell>
                    <TableCell align='center'>
                      {row.delivery.length} ({row.delivery.length})
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                {protocolsInMonth.map((row) => (
                  <TableRow
                    key={row.date}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => onDateClick(row.date)}
                  >
                    <TableCell component='th' scope='row'>
                      {dayjs(row.date).format('DD.MM.YYYY')}
                    </TableCell>
                    <TableCell align='center'>
                      {row.acceptance.length} ({row.acceptance.length})
                    </TableCell>
                    <TableCell align='center'>
                      {row.delivery.length} ({row.delivery.length})
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  )
}

export default SelectProtocolByMonth
